import { Injectable } from '@angular/core';

import { IFacade } from '@core/store/facade';
import { Classroom } from '@models/classroom';
import { IClassroomContext } from '@models/classroom-context';
import { ClassroomIcon } from '@models/classroom-icon';
import { ClassroomPin } from '@models/classroom-pin';
import { SchoolLicense } from '@models/license/school-license';
import { IProductComponent } from '@models/product-component';
import { IProductLine } from '@models/product-line';
import { IProductVariant } from '@models/product-variant';
import { Quest } from '@models/quest';
import { QuestCollection } from '@models/quest-collection';
import { IUser } from '@models/user';
import { IViewerAsset } from '@models/viewer-asset';
import { Action, Store } from '@ngrx/store';
import { GradeType } from '@shared/enums/grade-type';
import { LicenseAssignmentType } from '@shared/enums/license-status';
import { ProductType } from '@shared/enums/product-type';
import { VariantType } from '@shared/enums/variant-type';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import * as Actions from 'src/app/classrooms/store/learning.actions';
import { LearningState } from './learning.reducer';
import {
  selectClassroom,
  selectClassroomContext,
  selectClassroomIcons,
  selectClassroomIconsUnloaded,
  selectClassroomLicenses,
  selectClassroomPins,
  selectClassrooms,
  selectClassroomSaved,
  selectClassroomStudents,
  selectClassroomTeachers,
  selectClassroomParents,
  selectClassroomsUnloaded,
  selectErrors,
  selectPinState,
  selectProductLine,
  selectProductLines,
  selectProductLinesUnloaded,
  selectQuestLicensesFromClassroom,
  selectSchoolLicenses,
  selectSchoolLicensesUnloaded,
  selectStudentQuests,
  selectStudentQuestsFromProductClassroom,
  selectViewerAssets
} from './learning.selectors';

@Injectable({
  providedIn: 'root',
})

export class LearningFacade implements IFacade {
  productLines$: Observable<IProductLine[]>;
  classrooms$: Observable<Classroom[]>;
  classroomLicenses$: Observable<SchoolLicense[]>;
  classroomIcons$: Observable<ClassroomIcon[]>;
  schoolLicenses$: Observable<SchoolLicense[]>;
  studentQuests$: Observable<QuestCollection[]>;
  viewerAssets$: Observable<IViewerAsset[]>;
  errors$: Observable<string[]>;
  classroomContext$: Observable<IClassroomContext>;
  selectedClassroom$: Observable<Classroom>;
  canRefreshProductLines$: Observable<boolean>;
  canRefreshClassrooms$: Observable<boolean>;
  canRefreshClassroomIcons$: Observable<boolean>;
  canRefreshSchoolLicenses$: Observable<boolean>;
  classroomHasBeenSaved$: Observable<boolean>;
  canCheckForRedirect$: Observable<boolean>;
  classroomPins$: Observable<ClassroomPin[]>;
  pinState$: Observable<boolean>;

  constructor(private store: Store<LearningState>) {
    this.productLines$ = this.store.select(selectProductLines);
    this.canRefreshProductLines$ = this.store.select(selectProductLinesUnloaded);
    this.canRefreshClassrooms$ = this.store.select(selectClassroomsUnloaded);
    this.canRefreshClassroomIcons$ = this.store.select(selectClassroomIconsUnloaded);
    this.canRefreshSchoolLicenses$ = this.store.select(selectSchoolLicensesUnloaded);
    this.classroomHasBeenSaved$ = this.store.select(selectClassroomSaved);
    this.classrooms$ = this.store.select(selectClassrooms);
    this.classroomLicenses$ = this.getClassroomLicenses();
    this.classroomIcons$ = this.store.select(selectClassroomIcons);
    this.schoolLicenses$ = this.store.select(selectSchoolLicenses);
    this.classroomContext$ = this.store.select(selectClassroomContext);
    this.selectedClassroom$ = this.getClassroom();
    this.studentQuests$ = this.store.select(selectStudentQuests);
    this.viewerAssets$ = this.store.select(selectViewerAssets);
    this.errors$ = this.store.select(selectErrors);
    this.classroomPins$ = this.store.select(selectClassroomPins);
    this.pinState$ = this.store.select(selectPinState);
  }

  /**
   * @deprecated keep dispatch calls encapsulated inside the facade.
   */
  dispatch(action: Action): void {
    this.store.dispatch(action);
  }

  selectWithProps(func: Function, ...props: any[]): Observable<any> {
    return this.store.select(func(...props));
  }

  loadClassroomProfiles(forceLoad: boolean = false): void {
    this.store.dispatch(Actions.loadClassroomProfiles({ forceLoad }));
  }

  initClassrooms(): void {
    this.store.dispatch(Actions.initClassrooms());
  }

  initProductLines(resetCache: boolean = false): void {
    this.store.dispatch(Actions.initProductLines({ resetCache }));
  }

  loadViewerAssets(productType: ProductType, variantType: VariantType): void {
    this.store.dispatch(Actions.loadViewerAssets({ productType, variantType }));
  }

  /**
   * Puts the classroom into the classroom context, which means it is the classroom of focus for the user.
   * Also loads data related to the classroom, such as licenses and students, and puts them into the context.
   */
  selectClassroom(classroom: Classroom): void {
    this.store.dispatch(Actions.selectClassroom({ classroom }));
  }

  removeClassroom(classroomId: string) {
    this.store.dispatch(Actions.removeClassroom({ classroomId }));
  }

  /**
   * @deprecated prefer {@link selectedClassroom$} since this method has to build/look-up the observable every call.
   */
  getClassroom(): Observable<Classroom> {
    return this.store.select(selectClassroom);
  }

  getClassroomStudents(): Observable<IUser[]> {
    return this.store.select(selectClassroomStudents);
  }

  getClassroomTeachers(): Observable<IUser[]> {
    return this.store.select(selectClassroomTeachers);
  }

  getClassroomParents(): Observable<IUser[]> {
    return this.store.select(selectClassroomParents);
  }

  setClassroomStudents(students: IUser[]): void {
    this.store.dispatch(Actions.setClassroomStudents({ students }));
  }

  /**
   * Updates the student with the matching `userId` in the classroom,
   * or adds the student to the end of the list if no matching student is found.
   */
  upsertClassroomStudent(updatedStudent: IUser): void {
    this.store.dispatch(Actions.upsertClassroomStudent({ updatedStudent }));
  }

  removeClassroomStudent(studentToRemove: IUser): void {
    this.store.dispatch(Actions.removeClassroomStudent({ studentToRemove }));
  }

  removeClassroomStudents(): void {
    this.store.dispatch(Actions.removeClassroomStudents());
  }

  /**
   * Updates the teacher with the matching `userId` in the classroom,
   * or adds the teacher to the end of the list if no matching teacher is found.
   */
  upsertClassroomTeacher(updatedTeacher: IUser): void {
    this.store.dispatch(Actions.upsertClassroomTeacher({ updatedTeacher }));
  }

  removeClassroomTeacher(teacherToRemove: IUser): void {
    this.store.dispatch(Actions.removeClassroomTeacher({ teacherToRemove }));
  }

  /**
   * @deprecated prefer {@link classroomLicenses$} since this method has to build/look-up the observable every call.
   */
  getClassroomLicenses(): Observable<SchoolLicense[]> {
    return this.store.select(selectClassroomLicenses);
  }

  setClassroomLicenses(licenses: SchoolLicense[]): void {
    this.store.dispatch(Actions.setClassroomLicenses({ licenses }));
  }

  reloadClassroomLicenses(classroom: Classroom): void {
    this.store.dispatch(Actions.reloadClassroomLicenses({ classroom }));
  }

  getProductLineByKey(productLineKey: string): Observable<IProductLine> {
    return this.selectWithProps(selectProductLine, productLineKey);
  }

  getProductLineVariant(productLineKey: string, variantType: VariantType): Observable<IProductVariant> {
    return this.getProductLineByKey(productLineKey)
      .pipe(
        map(productLine => (productLine ? productLine.variants.find(v => v.variantType === variantType) : null)),
      );
  }

  /**
   * Returns a product component based on its component identifier string from any matching SKU (Product).
   */
  getProductComponentByComponentId(productLineKey: string, variantType: VariantType, componentId: string, licenseType: LicenseAssignmentType): Observable<IProductComponent> {
    return this.getProductLineVariant(productLineKey, variantType)
      .pipe(
        map((variant) => {
          if (variant) {
            return variant.skus
              .filter(sku => sku.licenseAssignmentType === licenseType)
              .reduce((ret: IProductComponent, sku): IProductComponent => {
                if (!ret) {
                  const component = sku.components.find(c => c.componentId === componentId);
                  if (component) {
                    return { ...component, product: sku };
                  }
                }
                return ret;
              }, null);
          }
          return null;
        })
      );
  }

  /**
   * Returns a product component based on its guid from any matching SKU (Product).
   */
  getProductComponentByProductId(productLineKey: string, variantType: VariantType, productId: string, licenseType: LicenseAssignmentType): Observable<IProductComponent> {
    return this.getProductLineVariant(productLineKey, variantType)
      .pipe(
        map((variant) => {
          if (variant) {
            return variant.skus
              .filter(sku => sku.licenseAssignmentType === licenseType)
              .reduce((ret: IProductComponent, sku): IProductComponent => {
                if (!ret) {
                  const component = sku.components.find(c => c.productId === productId);
                  if (component) {
                    return { ...component, product: sku };
                  }
                }
                return ret;
              }, null);
          }
          return null;
        })
      );
  }

  getQuestsByProductClassroom(productType: ProductType, classroomId: string, gradeType: GradeType | string): Observable<Quest[]> {
    return this.selectWithProps(selectStudentQuestsFromProductClassroom, productType, classroomId, gradeType);
  }

  getQuestProductsForClassroom(includeRestricted: boolean = false): Observable<SchoolLicense[]> {
    return this.selectWithProps(selectQuestLicensesFromClassroom, includeRestricted);
  }

  getClassroomPins(): Observable<ClassroomPin[]> {
    return this.store.select(selectClassroomPins);
  }
}
